/**
 * Minimal authenticated HTTP client used by the mapping frontend's
 * service-layer classes. Mirrors the pattern from the extraction
 * frontend so this module can later be wired to the same BeeHive
 * auth context (token comes from `setGlobalAccessTokenFn`, falling back to
 * the in-memory token store).
 */
import { getAccessTokenSync } from "./tokenStore";

export interface HttpClientOptions extends RequestInit {
  baseUrl?: string;
}

export interface ApiErrorPayload {
  error_code?: string;
  message?: string;
  description?: string;
}

let globalGetAccessToken: (() => Promise<string | null>) | null = null;

export const setGlobalAccessTokenFn = (
  fn: () => Promise<string | null>,
): void => {
  globalGetAccessToken = fn;
};

export class ApiError extends Error {
  public readonly status: number;
  public readonly errorCode: string | undefined;
  public readonly description: string | undefined;

  constructor(
    message: string,
    status: number,
    errorCode?: string,
    description?: string,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.errorCode = errorCode;
    this.description = description;
  }
}

export class HttpClient {
  private baseUrl: string;
  private getAccessTokenFn?: () => Promise<string | undefined>;

  constructor(
    baseUrl: string = "",
    getAccessTokenFn?: () => Promise<string | undefined>,
  ) {
    this.baseUrl = baseUrl;
    this.getAccessTokenFn = getAccessTokenFn;
  }

  private async getAccessToken(): Promise<string | null> {
    try {
      if (this.getAccessTokenFn) {
        const token = await this.getAccessTokenFn();
        if (token) return token;
      }
      if (globalGetAccessToken) {
        const token = await globalGetAccessToken();
        if (token) return token;
      }
      return getAccessTokenSync();
    } catch (error) {
      console.error("Error getting access token:", error);
      return getAccessTokenSync();
    }
  }

  public async makeAuthenticatedRequest<T>(
    endpoint: string,
    options: HttpClientOptions = {},
  ): Promise<T> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new ApiError(
        "No access token available. Please log in.",
        401,
        "unauthenticated",
      );
    }

    const { baseUrl, ...fetchOptions } = options;
    const url = (baseUrl || this.baseUrl) + endpoint;

    const response = await fetch(url, {
      ...fetchOptions,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...fetchOptions.headers,
      },
    });

    if (response.status === 401) {
      throw new ApiError(
        "Authentication failed. Please log in again.",
        401,
        "unauthenticated",
      );
    }

    if (!response.ok) {
      await this.handleErrorResponse(response);
    }

    return this.parseResponse<T>(response);
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    if (response.status === 204 || contentLength === "0") {
      return undefined as T;
    }

    if (!contentType?.includes("application/json")) {
      const text = await response.text();
      if (!text) return undefined as T;
      try {
        return JSON.parse(text) as T;
      } catch {
        return undefined as T;
      }
    }

    try {
      return (await response.json()) as T;
    } catch (error) {
      console.error("Error parsing JSON response:", error);
      return undefined as T;
    }
  }

  private async handleErrorResponse(response: Response): Promise<never> {
    const contentType = response.headers.get("content-type");

    if (contentType && contentType.includes("application/json")) {
      try {
        const errorData = (await response.json()) as ApiErrorPayload;
        throw new ApiError(
          errorData.message ?? `API request failed: ${response.status}`,
          response.status,
          errorData.error_code,
          errorData.description,
        );
      } catch (err) {
        if (err instanceof ApiError) throw err;
        // fall through to text-based error below
      }
    }

    const errorText = await response.text().catch(() => "");
    throw new ApiError(
      `API request failed: ${response.status} ${errorText || response.statusText}`,
      response.status,
    );
  }
}
